import { apiClient } from "./api";
import { LONG_TIMEOUT_MS } from "./httpTimeouts";
import type {
  PlayerRatingEntry,
  PlayerStatsResponse,
} from "../types/playerStats";

/**
 * Authenticated path: admin / analyst only.
 * Uses the shared axios instance (Firebase ID-token interceptor attached).
 *
 * EPIC-69 / PMA-ENTITY-020: LONG_TIMEOUT_MS, not the axios client default.
 * This is also the data source behind the CSV/PDF stats export in
 * `export.ts` (`exportPlayerStatsCSV`/`exportPlayerStatsPDF` both take an
 * already-fetched `PlayerStatsResponse` and generate their output locally,
 * with no network call of their own — this fetch is the epic's "PDF
 * export" long path). The backend loads every event for the match with an
 * unbounded `.to_list(length=None)` and runs two synchronous passes
 * (`compute_player_stats`, `compute_player_ratings`) before responding
 * (ProMatchAnalytics-Backend/app/routers/dashboard.py:149-157) — cost
 * proportional to match size, unlike the fixed single-document ops the
 * default timeout is sized for.
 */
export function fetchMatchPlayerStats(
  matchId: string,
): Promise<PlayerStatsResponse> {
  return apiClient.get<PlayerStatsResponse>(
    `/dashboard/matches/${matchId}/player-stats`,
    { timeout: LONG_TIMEOUT_MS },
  );
}

/**
 * EPIC-07 — Per-Player Match Score (Rating Engine).
 *
 * Pure player_id -> rating-fields lookup, derived from the SAME response
 * `fetchMatchPlayerStats` already returns. The rating has exactly ONE
 * source (the backend); this is a merge helper, never a re-derivation of
 * the formula. Kept pure and exported (rather than inlined in a hook's
 * `useMemo`) so the merge logic is unit-testable without React Query.
 *
 * Old-payload safety (mirrors the E4 xg/progressive_* convention): a
 * missing `rating`/`rating_breakdown` defaults to `null`, a missing
 * `rating_provisional` defaults to `false` — never coerced to a truthy
 * placeholder that could be mistaken for a real computed rating.
 */
export function buildPlayerRatingLookup(
  response: PlayerStatsResponse,
): Record<string, PlayerRatingEntry> {
  const lookup: Record<string, PlayerRatingEntry> = {};
  for (const p of response.players) {
    lookup[p.player_id] = {
      rating: p.rating ?? null,
      rating_provisional: p.rating_provisional ?? false,
      rating_breakdown: p.rating_breakdown ?? null,
    };
  }
  return lookup;
}
